import type { ActionDescriptor, ListResult, OrderDetail, OrderItem } from "@repair/contracts";
import { stateSchema } from "@repair/contracts";

type RepairRuntimeConfig = {
  customerApi?: string;
  adminApi?: string;
  wechatAuthApi?: string;
  shadowCustomerAuth?: boolean;
  shadowCustomerId?: string;
  shadowOpenid?: string;
};

function runtimeConfig(): RepairRuntimeConfig {
  return (window as typeof window & { REPAIR_CONFIG?: RepairRuntimeConfig }).REPAIR_CONFIG || {};
}

const CUSTOMER_API = runtimeConfig().customerApi || "/api/repair-orders";
const ADMIN_API = runtimeConfig().adminApi || "/api/admin/repair-orders";
const WECHAT_API = runtimeConfig().wechatAuthApi || "/api/wechat-user";
const CUSTOMER_AUTH_KEY = "repair_auth_context";
const ADMIN_TOKEN_KEY = "repair_admin_token";
const ADMIN_PROFILE_KEY = "repair_admin_profile";
const OAUTH_STATE_KEY = "repair_oauth_state";

export class ApiError extends Error {
  status: number;
  code?: string;
  details?: Record<string, unknown>;

  constructor(message: string, status: number, code?: string, details?: Record<string, unknown>) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

type ApiEnvelope<T> = {
  ok: boolean;
  message?: string;
  code?: string;
  data: T;
  details?: Record<string, unknown>;
};

async function post<T>(url: string, body: Record<string, unknown>, token?: string): Promise<ApiEnvelope<T>> {
  const response = await fetch(url, {
    method: "POST",
    credentials: "same-origin",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(body),
  });
  const text = await response.text();
  let payload: ApiEnvelope<T>;
  try {
    payload = text ? JSON.parse(text) : { ok: false, data: undefined as T };
  } catch {
    throw new ApiError(text || "服务返回格式异常", response.status);
  }
  if (!response.ok || payload.ok === false) {
    throw new ApiError(payload.message || "请求失败", response.status, payload.code, payload.details);
  }
  return payload;
}

function readJson<T>(key: string, fallback: T): T {
  try {
    return JSON.parse(localStorage.getItem(key) || "") as T;
  } catch {
    return fallback;
  }
}

export type CustomerSession = {
  ready: boolean;
  customerId: string;
  openid: string;
  sessionToken: string;
  sessionExpiresAt: string;
};

function storedCustomerSession(): CustomerSession {
  const raw = readJson<Record<string, string>>(CUSTOMER_AUTH_KEY, {});
  const sessionExpiresAt = raw.sessionExpiresAt || raw.session_expires_at || "";
  return {
    ready: Boolean(raw.sessionToken || raw.session_token),
    customerId: raw.customerId || raw.customer_id || "",
    openid: raw.openid || "",
    sessionToken: raw.sessionToken || raw.session_token || "",
    sessionExpiresAt,
  };
}

function isFresh(session: CustomerSession) {
  if (!session.ready || !session.sessionToken) return false;
  const expiresAt = new Date(session.sessionExpiresAt).getTime();
  return !Number.isFinite(expiresAt) || expiresAt > Date.now() + 5 * 60_000;
}

function storeCustomerSession(data: Record<string, string>): CustomerSession {
  const session: CustomerSession = {
    ready: true,
    customerId: data.customerId || data.customer_id || "",
    openid: data.openid || "",
    sessionToken: data.session_token || data.sessionToken || "",
    sessionExpiresAt: data.session_expires_at || data.sessionExpiresAt || "",
  };
  localStorage.setItem(CUSTOMER_AUTH_KEY, JSON.stringify(session));
  return session;
}

function randomState() {
  const bytes = crypto.getRandomValues(new Uint8Array(24));
  return Array.from(bytes, (value) => value.toString(16).padStart(2, "0")).join("");
}

function cleanOAuthQuery() {
  const url = new URL(window.location.href);
  url.searchParams.delete("code");
  url.searchParams.delete("state");
  window.history.replaceState({}, "", `${url.pathname}${url.search}${url.hash}`);
}

export async function bootstrapCustomerAuth(): Promise<CustomerSession> {
  const stored = storedCustomerSession();
  const params = new URLSearchParams(window.location.search);
  const code = params.get("code");
  if (!code && isFresh(stored)) return stored;

  if (code) {
    const returnedState = params.get("state") || "";
    const expectedState = sessionStorage.getItem(OAUTH_STATE_KEY) || "";
    sessionStorage.removeItem(OAUTH_STATE_KEY);
    if (!expectedState || returnedState !== expectedState) {
      cleanOAuthQuery();
      throw new ApiError("微信授权校验失败，请从公众号菜单重新进入", 401, "OAUTH_STATE_MISMATCH");
    }
    const result = await post<Record<string, string>>(WECHAT_API, { mode: "exchange", code });
    cleanOAuthQuery();
    return storeCustomerSession(result.data);
  }

  const config = runtimeConfig();
  const isLocalShadowHost = ["127.0.0.1", "localhost"].includes(window.location.hostname);
  if (config.shadowCustomerAuth === true && isLocalShadowHost) {
    const result = await post<Record<string, string>>(WECHAT_API, {
      mode: "exchange",
      openid: config.shadowOpenid || "shadow-preview-openid",
      customer_id: config.shadowCustomerId || "C_SHADOW_PREVIEW",
    });
    return storeCustomerSession(result.data);
  }

  if (/MicroMessenger/i.test(navigator.userAgent)) {
    const state = randomState();
    sessionStorage.setItem(OAUTH_STATE_KEY, state);
    const result = await post<{ authorize_url: string }>(WECHAT_API, {
      mode: "authorize",
      state,
      redirect_uri: window.location.href,
    });
    window.location.assign(result.data.authorize_url);
  }
  return { ready: false, customerId: "", openid: "", sessionToken: "", sessionExpiresAt: "" };
}

export function customerSession() {
  return storedCustomerSession();
}

function finiteNumber(value: unknown, fallback = 0) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function normalizeDetail(data: OrderDetail) {
  const quote = data.quote
    ? {
        ...data.quote,
        version_no: finiteNumber(data.quote.version_no),
        total_amount: finiteNumber(data.quote.total_amount),
        estimated_days: data.quote.estimated_days == null ? null : finiteNumber(data.quote.estimated_days),
        items: Array.isArray(data.quote.items)
          ? data.quote.items.map((item) => {
              const quantity = finiteNumber(item.quantity);
              const unitPrice = finiteNumber(item.unit_price);
              return {
                ...item,
                quantity,
                unit_price: unitPrice,
                amount: finiteNumber(item.amount, quantity * unitPrice),
              };
            })
          : [],
      }
    : null;
  return { ...data, state: stateSchema.parse(data.state), quote };
}

export const customerApi = {
  async list(status = "全部", limit = 50, offset = 0) {
    const session = storedCustomerSession();
    const result = await post<ListResult<OrderItem>>(CUSTOMER_API, { mode: "list", status, limit, offset }, session.sessionToken);
    return result.data;
  },
  async detail(id: string) {
    const session = storedCustomerSession();
    const result = await post<OrderDetail>(CUSTOMER_API, { mode: "detail", id }, session.sessionToken);
    return normalizeDetail(result.data);
  },
  async create(payload: Record<string, unknown>) {
    const session = storedCustomerSession();
    const result = await post<{ id: string; order_no: string; current_status: string }>(
      CUSTOMER_API,
      { ...payload, mode: "create" },
      session.sessionToken,
    );
    return result.data;
  },
  async execute(id: string, action: ActionDescriptor, version: number, payload: Record<string, unknown>) {
    const session = storedCustomerSession();
    const result = await post<OrderDetail>(
      CUSTOMER_API,
      { ...payload, mode: "execute_action", order_id: id, action_code: action.code, expected_version: version, payload },
      session.sessionToken,
    );
    return normalizeDetail(result.data);
  },
  async prepareUpload(orderId: string, file: File) {
    const session = storedCustomerSession();
    const result = await post<{ items: UploadTicket[] }>(
      CUSTOMER_API,
      {
        mode: "prepare_attachment_upload",
        order_id: orderId,
        attachments: [{ file_name: file.name, file_size: file.size, mime_type: file.type || "application/octet-stream" }],
      },
      session.sessionToken,
    );
    return result.data.items[0];
  },
  async attach(orderId: string, ticket: UploadTicket, category: string) {
    const session = storedCustomerSession();
    await post<OrderDetail>(
      CUSTOMER_API,
      { mode: "upload_attachment", order_id: orderId, upload_token: ticket.upload_token, category },
      session.sessionToken,
    );
  },
};

export type AdminProfile = {
  username: string;
  operatorName: string;
  operatorRole: string;
  role: string;
  expire_at?: string;
};

export type AdminAssignee = {
  id: string;
  username: string;
  display_name: string;
  role: string;
};

export function adminSession() {
  return {
    token: localStorage.getItem(ADMIN_TOKEN_KEY) || "",
    profile: readJson<AdminProfile | null>(ADMIN_PROFILE_KEY, null),
  };
}

export function clearAdminSession() {
  localStorage.removeItem(ADMIN_TOKEN_KEY);
  localStorage.removeItem(ADMIN_PROFILE_KEY);
}

function adminToken() {
  return adminSession().token;
}

export const adminApi = {
  async login(username: string, password: string) {
    const result = await post<{ token: string; profile: AdminProfile }>(ADMIN_API, { mode: "login", username, password });
    localStorage.setItem(ADMIN_TOKEN_KEY, result.data.token);
    localStorage.setItem(ADMIN_PROFILE_KEY, JSON.stringify(result.data.profile));
    return result.data;
  },
  async session() {
    const result = await post<{ profile: AdminProfile }>(ADMIN_API, { mode: "session" }, adminToken());
    localStorage.setItem(ADMIN_PROFILE_KEY, JSON.stringify(result.data.profile));
    return result.data.profile;
  },
  async list(payload: Record<string, unknown> = {}) {
    const result = await post<ListResult<OrderItem> & Record<string, unknown>>(ADMIN_API, { limit: 50, ...payload, mode: "list" }, adminToken());
    return result.data;
  },
  async detail(id: string) {
    const result = await post<OrderDetail>(ADMIN_API, { mode: "detail", id }, adminToken());
    return normalizeDetail(result.data);
  },
  async execute(id: string, action: ActionDescriptor, version: number, payload: Record<string, unknown>) {
    const result = await post<OrderDetail>(
      ADMIN_API,
      { ...payload, mode: "execute_action", order_id: id, action_code: action.code, expected_version: version, payload },
      adminToken(),
    );
    return normalizeDetail(result.data);
  },
  async dashboard() {
    const result = await post<Record<string, unknown>>(ADMIN_API, { mode: "dashboard_summary" }, adminToken());
    return result.data;
  },
  async users() {
    const result = await post<Record<string, unknown>>(ADMIN_API, { mode: "list_admin_users" }, adminToken());
    return result.data;
  },
  async assignees() {
    const result = await post<{ items: AdminAssignee[]; total: number }>(ADMIN_API, { mode: "list_assignees" }, adminToken());
    return result.data;
  },
  async createUser(payload: Record<string, unknown>) {
    const result = await post<Record<string, unknown>>(ADMIN_API, { mode: "create_admin_user", ...payload }, adminToken());
    return result.data;
  },
  async updateUser(payload: Record<string, unknown>) {
    const result = await post<Record<string, unknown>>(ADMIN_API, { mode: "update_admin_user", ...payload }, adminToken());
    return result.data;
  },
  async unlockUser(id: string) {
    const result = await post<Record<string, unknown>>(ADMIN_API, { mode: "unlock_admin_user", id }, adminToken());
    return result.data;
  },
  async deleteUser(id: string, confirmUsername: string) {
    const result = await post<Record<string, unknown>>(
      ADMIN_API,
      { mode: "delete_admin_user", id, confirm_username: confirmUsername },
      adminToken(),
    );
    return result.data;
  },
  async createRole(payload: Record<string, unknown>) {
    const result = await post<Record<string, unknown>>(ADMIN_API, { mode: "create_admin_role", ...payload }, adminToken());
    return result.data;
  },
  async updateRole(payload: Record<string, unknown>) {
    const result = await post<Record<string, unknown>>(ADMIN_API, { mode: "update_admin_role", ...payload }, adminToken());
    return result.data;
  },
  async deleteRole(id: string, confirmRoleName: string) {
    const result = await post<Record<string, unknown>>(
      ADMIN_API,
      { mode: "delete_admin_role", id, confirm_role_name: confirmRoleName },
      adminToken(),
    );
    return result.data;
  },
  async logs(kind: "operation" | "notification", payload: Record<string, unknown> = {}) {
    const mode = kind === "operation" ? "list_operation_logs" : "list_notification_logs";
    const result = await post<ListResult<Record<string, unknown>> & Record<string, unknown>>(ADMIN_API, { limit: 50, ...payload, mode }, adminToken());
    return result.data;
  },
  async exportOrders(payload: Record<string, unknown> = {}) {
    const result = await post<{ csv: string; file_name: string }>(ADMIN_API, { ...payload, mode: "export_orders" }, adminToken());
    return result.data;
  },
  async bulk(mode: "bulk_soft_delete_orders" | "bulk_restore_orders" | "bulk_hard_delete_orders", ids: string[], reason: string) {
    const result = await post<Record<string, unknown>>(
      ADMIN_API,
      {
        mode,
        ids,
        delete_reason: reason,
        restore_reason: reason,
        purge_reason: reason,
      },
      adminToken(),
    );
    return result.data;
  },
};

export type UploadTicket = {
  upload_url: string;
  upload_method?: string;
  upload_headers?: Record<string, string>;
  upload_token: string;
  cloud_path: string;
};

type UploadFileOptions = {
  /**
   * Abort when the browser has emitted no upload progress for this long.
   * This is an inactivity timeout so large videos can still finish on a slow
   * mobile connection as long as bytes continue to move.
   */
  stallTimeoutMs?: number;
  totalTimeoutMs?: number;
};

export function uploadFile(
  ticket: UploadTicket,
  file: File,
  onProgress?: (value: number) => void,
  options: UploadFileOptions = {},
) {
  const stallTimeoutMs = options.stallTimeoutMs ?? 90_000;
  const totalTimeoutMs = options.totalTimeoutMs ?? 15 * 60_000;

  return new Promise<void>((resolve, reject) => {
    const request = new XMLHttpRequest();
    let settled = false;
    let stallTimer: number | undefined;
    let abortMessage = "";

    const clearStallTimer = () => {
      if (stallTimer !== undefined) {
        window.clearTimeout(stallTimer);
        stallTimer = undefined;
      }
    };

    const cleanup = () => {
      clearStallTimer();
      request.onload = null;
      request.onerror = null;
      request.onabort = null;
      request.ontimeout = null;
      request.upload.onprogress = null;
    };

    const fail = (message: string, status = request.status || 0, code = "UPLOAD_FAILED") => {
      if (settled) return;
      settled = true;
      cleanup();
      reject(new ApiError(message, status, code));
    };

    const armStallTimer = () => {
      clearStallTimer();
      stallTimer = window.setTimeout(() => {
        abortMessage = "上传长时间没有进度，请检查网络后重试";
        request.abort();
      }, stallTimeoutMs);
    };

    request.open(ticket.upload_method || "PUT", ticket.upload_url, true);
    request.timeout = totalTimeoutMs;
    for (const [name, value] of Object.entries(ticket.upload_headers || {})) {
      request.setRequestHeader(name, value);
    }

    request.upload.onprogress = (event) => {
      armStallTimer();
      if (!event.lengthComputable || event.total <= 0) return;
      onProgress?.(Math.min(0.95, 0.05 + (event.loaded / event.total) * 0.9));
    };
    request.onload = () => {
      if (request.status < 200 || request.status >= 300) {
        fail(`文件上传失败（${request.status || "网络异常"}）`);
        return;
      }
      if (settled) return;
      settled = true;
      cleanup();
      onProgress?.(1);
      resolve();
    };
    request.onerror = () => fail("文件上传失败，请检查网络或稍后重试");
    request.ontimeout = () => fail("文件上传超时，请重试", request.status || 0, "UPLOAD_TIMEOUT");
    request.onabort = () => fail(
      abortMessage || "文件上传已取消",
      request.status || 0,
      abortMessage ? "UPLOAD_STALLED" : "UPLOAD_ABORTED",
    );

    onProgress?.(0.05);
    armStallTimer();
    request.send(file);
  });
}
